import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SongDTO, SongListItemDTO } from '../../shared/dto/library';
import { ApiFailure, api } from '../api/client';
import { SONGS_KEY } from '../api/library';

export const songKey = (id: string) => ['song', id] as const;

/** GET /api/songs/:id → SongDTO (the cache under ['song', id] always holds a bare SongDTO). */
export async function fetchSong(id: string): Promise<SongDTO> {
  const r = await api<SongDTO | { song: SongDTO }>(`/songs/${encodeURIComponent(id)}`);
  return 'song' in r && r.song && typeof r.song === 'object' ? r.song : (r as SongDTO);
}

/**
 * 403/404 for a song: deleted, or no longer visible to this user (audience narrowed). TanStack keeps
 * the old (persisted) data next to such an error — it must never be shown (see SongScreen).
 */
export function isSongGone(e: unknown): boolean {
  return e instanceof ApiFailure && (e.status === 404 || e.status === 403);
}

/**
 * The song page's data; persisted so a song opened once still opens offline. When the server says
 * the song is gone it also leaves the cached song list (like useEvent does for events).
 */
export function useSong(id: string | undefined) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: songKey(id ?? ''),
    queryFn: async () => {
      try {
        return await fetchSong(id!);
      } catch (e) {
        if (isSongGone(e)) {
          qc.setQueryData<SongListItemDTO[]>(SONGS_KEY, (list) => (list?.some((s) => s.id === id) ? list.filter((s) => s.id !== id) : list));
        }
        throw e;
      }
    },
    enabled: !!id,
    meta: { persist: true },
  });
}
